import React, { useState, useEffect, useContext } from 'react'
import { Button, Tabs, LoadingOverlay } from '@mantine/core'
import { Col, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh, faCalendarCheck } from '@fortawesome/free-solid-svg-icons' // Removed faUpload since upload is disabled
import { toast } from 'react-toastify'
import { KTCard, KTCardBody } from '../../../_metronic/helpers'
import { useAuth } from '../../modules/auth'
import roleContext from '../../context/roleContext'
import { RoleContextValue } from '../../types/OtherTypes'
import { getTeamMemberData } from '../../CommonFunction'
import AttendanceTable from '../../components/attendance/AttendanceTable'
import AttendanceLogs from '../../components/attendance/AttendanceLogs'
import AttendanceDashboard from './AttendanceDashboard'
// Upload-related imports commented out since bulk upload is disabled
// import {uploadAttendenceFile} from '../../../api/AttendanceAPI'
// import {Modal} from 'react-bootstrap'
// import CustomFileInput from '../../components/CustomInput/CustomFileInput'
// import SubmitCancleButton from '../../components/SubmitCancelButton'
// import {InputErrorType} from '../../types/OtherTypes'

interface EmployeeOption {
  value: string
  label: string
}

const EnhancedAttendanceManagement: React.FC = () => {
  const { currentUser } = useAuth()
  const roleState: RoleContextValue = useContext(roleContext)

  // Check if user is System Admin (admin@ctrine.com or admin@ctrine.in) or Super Admin Role
  const isSystemAdmin = currentUser?.email?.toLowerCase() === 'admin@ctrine.com' ||
    currentUser?.email?.toLowerCase() === 'admin@ctrine.in'
  const isSuperAdmin = roleState.roleName === 'Super Admin' || roleState.roleName === 'SUPER_ADMIN'
  // const canViewAMSLogs = isSystemAdmin || isSuperAdmin
  const isHR = roleState.roleName === 'HR' || roleState.roleName === 'Hr'
  const canViewAMSLogs =currentUser?.isRmOrHr || isSuperAdmin || isSystemAdmin
  // State management
  const [activeTab, setActiveTab] = useState<string>('records')
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [loading, setLoading] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // const isHR = roleState.roleName === 'HR' || roleState.roleName === 'Hr'
  // const isAdminRole = roleState.roleName === 'Admin' || roleState.roleName === 'ADMIN'

  // const canViewAMSDashboard = isSystemAdmin || isSuperAdmin || isHR || isAdminRole

const canViewAMSDashboard =
  currentUser?.isRmOrHr || isSuperAdmin || isSystemAdmin

  // Upload modal state - COMMENTED OUT since bulk upload is disabled
  // const [showUploadModal, setShowUploadModal] = useState(false)
  // const [uploadLoading, setUploadLoading] = useState(false)
  // const [uploadFormData, setUploadFormData] = useState<{file: File | null}>({file: null})
  // const [uploadError, setUploadError] = useState<InputErrorType>({})

  // Load team members for employee dropdown
  useEffect(() => {
    let mounted = true

    const loadEmployees = async () => {
      if (mounted && currentUser?.id) {
        try {
          setLoading(true)
          await getTeamMemberData(setEmployees, currentUser.id)
        } catch (error) {
          console.error('Failed to load team members:', error)
          toast.error('Failed to load employee list')
        } finally {
          setLoading(false)
        }
      }
    }

    loadEmployees()

    return () => {
      mounted = false
    }
  }, [currentUser?.id])

  // Handle file upload - COMMENTED OUT since bulk upload is disabled
  /*
  const handleFileUpload = async () => {
    if (!uploadFormData.file) {
      setUploadError({file: 'Please select a file to upload'})
      return
    }

    setUploadLoading(true)
    setUploadError({})

    try {
      const result = await uploadAttendenceFile(uploadFormData)
      
      if (result.statusCode === 200) {
        toast.success(result.message || 'File uploaded successfully')
        setShowUploadModal(false)
        setUploadFormData({file: null})
        
        // Trigger refresh of attendance records
        setRefreshTrigger(prev => prev + 1)
      } else {
        toast.error(result.message || 'Failed to upload file')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload file')
    } finally {
      setUploadLoading(false)
    }
  }

  const handleUploadInputChange = (payload: File | null) => {
    setUploadFormData({file: payload})
    
    // Clear error
    if (uploadError.file) {
      setUploadError({})
    }
  }
  */

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1)
    toast.info('Refreshing attendance records...')
  }

  // closeUploadModal function - COMMENTED OUT since bulk upload is disabled
  /*
  const closeUploadModal = () => {
    setShowUploadModal(false)
    setUploadFormData({file: null})
    setUploadError({})
  }
  */

  return (
    <div>
      <LoadingOverlay visible={loading} />

      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h1 className="page-heading" style={{ fontSize: '18px', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: '24px', color: '#3B82F6' }} />
            Attendance Management
          </h1>
          <p className="text-muted" style={{ fontSize: '12px', marginBottom: '0' }}>
            Comprehensive attendance tracking with CRUD operations, bulk upload, and payroll integration
          </p>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="outline"
            color="blue"
            onClick={handleRefresh}
            title="Refresh Data"
            size="xs"
          >
            <FontAwesomeIcon icon={faRefresh} className="me-1" style={{ fontSize: '10px' }} />
            Refresh
          </Button>

          {/* Upload CSV functionality commented out - Single record operations only */}
          {/* <Button
            variant="filled"
            color="green"
            onClick={() => setShowUploadModal(true)}
          >
            <FontAwesomeIcon icon={faUpload} className="me-2" />
            Upload CSV/Excel
          </Button> */}
        </div>
      </div>

      {/* Main Content */}
      <KTCard>
        <KTCardBody>
          <Tabs value={activeTab} onTabChange={(value) => setActiveTab(value ?? 'records')}>
            <Tabs.List style={{ display: 'flex', gap: '12px', marginBottom: '20px', padding: '0' }}>
              {/* <Tabs.Tab value="summary">
              Attendance Summary
            </Tabs.Tab> */}
              <Tabs.Tab
                value="records"
                style={{
                  padding: '10px 20px',
                  borderRadius: '16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: activeTab === 'records' ? 'rgba(255, 255, 255, 0.35)' : 'transparent',
                  backdropFilter: 'blur(12px)',
                  border: activeTab === 'records' ? '1px solid rgba(255, 255, 255, 0.65)' : '1px solid transparent',
                  boxShadow: activeTab === 'records' ? '0 4px 12px rgba(59, 130, 246, 0.15), 0 2px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  color: activeTab === 'records' ? '#2563EB' : '#64748B',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                Attendance Records
              </Tabs.Tab>
              {/* AMS Logs - Only visible to System Admin (admin@ctrine.com, admin@ctrine.in) or Super Admin Role */}
              {canViewAMSLogs && (
                <Tabs.Tab
                  value="ams-logs"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    backgroundColor: activeTab === 'ams-logs' ? 'rgba(255, 255, 255, 0.35)' : 'transparent',
                    backdropFilter: 'blur(12px)',
                    border: activeTab === 'ams-logs' ? '1px solid rgba(255, 255, 255, 0.65)' : '1px solid transparent',
                    boxShadow: activeTab === 'ams-logs' ? '0 4px 12px rgba(59, 130, 246, 0.15), 0 2px 4px rgba(0, 0, 0, 0.05)' : 'none',
                    color: activeTab === 'ams-logs' ? '#2563EB' : '#64748B',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  AMS Logs
                </Tabs.Tab>
              )}
              {/* <Tabs.Tab value="summary">
                Ams Dashboard
              </Tabs.Tab> */}
              {canViewAMSDashboard && (
                <Tabs.Tab
                  value="summary"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    backgroundColor: activeTab === 'summary' ? 'rgba(255, 255, 255, 0.35)' : 'transparent',
                    backdropFilter: 'blur(12px)',
                    border: activeTab === 'summary' ? '1px solid rgba(255, 255, 255, 0.65)' : '1px solid transparent',
                    boxShadow: activeTab === 'summary' ? '0 4px 12px rgba(59, 130, 246, 0.15), 0 2px 4px rgba(0, 0, 0, 0.05)' : 'none',
                    color: activeTab === 'summary' ? '#2563EB' : '#64748B',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  Ams Dashboard
                </Tabs.Tab>
              )}

              {/* Bulk Operations tab commented out - Single record operations only */}
              {/* <Tabs.Tab value="bulk-operations">
                📤 Bulk Operations
              </Tabs.Tab> */}
              {/* Temporarily hidden as requested */}
              {/* <Tabs.Tab value="reports">
                Reports & Analytics
              </Tabs.Tab> */}
            </Tabs.List>

            {/* TAB 2: SUMMARY */}
            {/* <Tabs.Panel value="summary" pt="lg">
            <AttendanceSummary />
          </Tabs.Panel> */}
            {/* <Tabs.Panel value="summary" pt="lg">
              <AttendanceDashboard />
            </Tabs.Panel> */}
            {canViewAMSDashboard && (
              <Tabs.Panel value="summary" pt="lg">
                <AttendanceDashboard />
              </Tabs.Panel>
            )}

            <Tabs.Panel value="records" pt="lg">
              <AttendanceTable
                employees={employees}
                refreshTrigger={refreshTrigger}
              />
            </Tabs.Panel>
            {/* AMS Logs panel - Only visible to System Admin (admin@ctrine.com, admin@ctrine.in) or Super Admin Role */}
            {canViewAMSLogs && (
              <Tabs.Panel value="ams-logs" pt="lg">
                <AttendanceLogs refreshTrigger={refreshTrigger} />
              </Tabs.Panel>
            )}


            {/* Bulk Operations panel commented out - Single record operations only */}
            {/* <Tabs.Panel value="bulk-operations" pt="lg">
              <div className="text-center py-5">
                <i className="fa fa-upload fa-3x text-muted mb-3"></i>
                <h4>Bulk Operations</h4>
                <p className="text-muted mb-4">
                  Upload multiple attendance records via CSV or Excel files
                </p>
                
                <div className="row justify-content-center">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-body">
                        <Button
                          size="lg"
                          fullWidth
                          onClick={() => setShowUploadModal(true)}
                        >
                          <FontAwesomeIcon icon={faUpload} className="me-2" />
                          Upload Attendance File
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tabs.Panel> */}

            {/* Temporarily hidden as requested */}
            {/* <Tabs.Panel value="reports" pt="lg">
              <div className="text-center py-5">
                <i className="fa fa-chart-line fa-3x text-muted mb-3"></i>
                <h4>Reports & Analytics</h4>
                <p className="text-muted mb-4">
                  Comprehensive attendance reports and payroll integration
                </p>
                
                <div className="row justify-content-center">
                  <div className="col-md-8">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="card h-100">
                          <div className="card-body text-center">
                            <i className="fa fa-users fa-2x text-primary mb-3"></i>
                            <h6>Employee Attendance</h6>
                            <p className="text-muted small">
                              Individual attendance tracking and summaries
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <div className="card h-100">
                          <div className="card-body text-center">
                            <i className="fa fa-money-bill fa-2x text-success mb-3"></i>
                            <h6>Payroll Integration</h6>
                            <p className="text-muted small">
                              Attendance data formatted for payroll processing
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <div className="card h-100">
                          <div className="card-body text-center">
                            <i className="fa fa-clock fa-2x text-warning mb-3"></i>
                            <h6>Overtime Tracking</h6>
                            <p className="text-muted small">
                              Monitor overtime hours and calculate compensation
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <div className="card h-100">
                          <div className="card-body text-center">
                            <i className="fa fa-calendar-check fa-2x text-info mb-3"></i>
                            <h6>Attendance Analytics</h6>
                            <p className="text-muted small">
                              Detailed insights into attendance patterns
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tabs.Panel> */}
          </Tabs>
        </KTCardBody>
      </KTCard>

      {/* Upload Modal - COMMENTED OUT since bulk upload is disabled */}
      {/* <Modal 
        show={showUploadModal} 
        onHide={closeUploadModal}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Attendance File</Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          <div className="mb-4">
            <p className="text-muted">
              Upload attendance records from CSV or Excel files. Make sure your file includes 
              the required columns: Employee Code, Date, In Time, Out Time, Status.
            </p>
          </div>
          
          <Row>
            <Col md={12}>
              <CustomFileInput
                name="file"
                value={uploadFormData.file}
                onChange={handleUploadInputChange}
                label="Attendance File"
                id="attendance-file"
                error={uploadError.file}
                accept=".csv,.xlsx,.xls"
              />
            </Col>
          </Row>
          
          {uploadFormData.file && (
            <div className="mt-3 p-3 bg-light rounded">
              <small className="text-muted">
                <strong>Selected File:</strong> {uploadFormData.file.name}
                <br />
                <strong>Size:</strong> {(uploadFormData.file.size / 1024 / 1024).toFixed(2)} MB
              </small>
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <SubmitCancleButton
            cancle={{
              onClick: closeUploadModal,
              label: 'Cancel'
            }}
            submit={{
              editid: null,
              loading: uploadLoading,
              onClick: handleFileUpload,
              label: 'Upload File'
            }}
          />
        </Modal.Footer>
      </Modal> */}
    </div>
  )
}

export default EnhancedAttendanceManagement
